import React, { useCallback, useState } from 'react';
import _isNil from 'lodash/isNil';
import { useLocation } from 'react-router-dom';
import { Business } from './BusinessType';
import { PositionManagementContextProvider, usePositionManagementContext } from './PositionManagementContext';
import { useStoreActions, useStoreState } from '../../../models/hooks';
import { BusinessListView } from './businessList/BusinessListView';
import { PositionList } from './positionList/PositionList';

interface LocationState {
  cameFromTopCandidate: boolean;
}

interface BusinessTabProps {
  resetActiveBusiness: boolean;
}

function BusinessTabComponent({ resetActiveBusiness }: BusinessTabProps): JSX.Element {
  const location = useLocation();
  const [filterType, setFilterType] = useState<'all' | 'followed' | 'watched' | 'followAndWatch'>('followAndWatch');

  const { positionQueryParams, setPositionQueryParams } = usePositionManagementContext();
  const [scrollToBusiness, setScrollToBusiness] = useState<Business | null>(null);
  const appUserId = useStoreState((s) => s.app.user?.id);
  const appOwnersId = useStoreState((s) => s.app.accounts?.owners);
  const currentActiveBusinessFromStore = useStoreState((s) => s.positionManagement.currentSelectedActiveBusiness);
  const setCurrentSelectedActiveBusiness = useStoreActions(
    (actions) => actions.positionManagement.setCurrentSelectedActiveBusiness,
  );

  const isAdministrationAccount = !_isNil(appOwnersId) && !_isNil(appUserId) && appOwnersId?.includes(appUserId);

  React.useEffect(() => {
    if (resetActiveBusiness) {
      setCurrentSelectedActiveBusiness(null);
    }
  }, [resetActiveBusiness, setCurrentSelectedActiveBusiness]);

  const changeActiveBusiness = useCallback(
    (business: Business | null) => {
      const newpositionQueryParams = { ...positionQueryParams };
      newpositionQueryParams.positionStatus = 'open';
      newpositionQueryParams.archive = false;
      setPositionQueryParams(newpositionQueryParams);
      setCurrentSelectedActiveBusiness(business);
    },
    [positionQueryParams, setPositionQueryParams, setCurrentSelectedActiveBusiness],
  );

  React.useEffect(() => {
    const locationState = location.state as LocationState;
    const cameFromTopCandidates = locationState?.cameFromTopCandidate;

    if (!cameFromTopCandidates && !currentActiveBusinessFromStore) {
      setCurrentSelectedActiveBusiness(null);
    }
  }, [location, currentActiveBusinessFromStore, setCurrentSelectedActiveBusiness]);

  return (
    <>
      <BusinessListView
        changeActiveBusiness={changeActiveBusiness}
        isAdministrationAccount={isAdministrationAccount}
        activeBusiness={currentActiveBusinessFromStore}
        scrollToBusiness={scrollToBusiness}
        setScrollToBusiness={setScrollToBusiness}
        setFilterType={setFilterType}
      />
      {currentActiveBusinessFromStore && (
        <PositionList
          activeBusiness={currentActiveBusinessFromStore}
          isAdministrationAccount={isAdministrationAccount}
          changeActiveBusiness={changeActiveBusiness}
          setScrollToBusiness={setScrollToBusiness}
          filterType={filterType}
        />
      )}
    </>
  );
}

function WrapperBusinessTab({ resetActiveBusiness }: BusinessTabProps) {
  return (
    <PositionManagementContextProvider>
      <BusinessTabComponent resetActiveBusiness={resetActiveBusiness} />
    </PositionManagementContextProvider>
  );
}

export const BusinessTab = React.memo(WrapperBusinessTab);
